<script setup>
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'StockOpname',
  },
})

const router = useRouter()

// ── State ────────────────────────────────────────────────────────────────────
const ingredients = ref([])
const ingredientsLoading = ref(false)
const ingredientSearch = ref('')

const form = ref({
  opname_date: new Date().toISOString().slice(0, 10),
  notes: '',
  ingredient_ids: [],
})

const loading = ref(false)
const errors = ref({})

// ── Fetch Ingredients ─────────────────────────────────────────────────────────────
const fetchIngredients = async (search = '') => {
  ingredientsLoading.value = true
  try {
    const params = new URLSearchParams({ per_page: 200 })
    if (search) params.set('search', search)

    const response = await useApi(`/api/v1/ingredients?${params}`).get()
    
    // Handle both paginated dan array langsung
    const data = response?.data ?? response
    ingredients.value = Array.isArray(data) 
      ? data 
      : (data?.data ?? [])
    
  } catch (err) {
    console.error('❌ fetchIngredients:', err)
  } finally {
    ingredientsLoading.value = false
  }
}

const debouncedSearch = useDebounceFn((val) => fetchIngredients(val), 300)
watch(ingredientSearch, debouncedSearch)
onMounted(() => fetchIngredients())

// ─── Select All ───────────────────────────────────────────────────────────────
const allSelected = computed(() =>
  ingredients.value.length > 0 &&
  form.value.ingredient_ids.length === ingredients.value.length
)

const someSelected = computed(() =>
  form.value.ingredient_ids.length > 0 && !allSelected.value
)

const toggleSelectAll = () => {
  form.value.ingredient_ids = allSelected.value
    ? []
    : ingredients.value.map(i => i.id)
}

// ─── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  loading.value = true
  errors.value = {}

  try {
    // ✅ FIX: Panggil .post() setelah useApi()
    const response = await useApi('/api/v1/stock-opnames').post({ 
      body: form.value 
    })

    // ✅ Response parsing yang benar
    const result = response?.data ?? response

    // ✅ Ambil ID dari dalam object 'data'
    const opnameId = result?.data?.id ?? result?.id

    if (!opnameId) {
      console.error('❌ ID tidak ditemukan di response:', result)
      errors.value = { _general: ['Gagal membuat opname. ID tidak ditemukan.'] }
      return
    }

    router.push({ name: 'inventory-stock-opnames-id', params: { id: opnameId } })
    
  } catch (err) {
    console.error('❌ create opname error:', err)
    if (err?.status === 422) {
      errors.value = err.data?.errors ?? {}
    } else {
      errors.value = { _general: [err?.data?.message ?? 'Terjadi kesalahan.'] }
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center mb-6">
        <VBtn variant="text" icon="ri-arrow-left-line" class="me-2" :to="{ name: 'inventory-stock-opnames' }" />
        <div>
          <h4 class="text-h4 font-weight-bold">Buat Sesi Stock Opname</h4>
          <p class="text-body-2 text-medium-emphasis mt-1">Pilih bahan baku yang akan dihitung secara fisik</p>
        </div>
      </div>
    </VCol>

    <!-- General Error -->
    <VCol v-if="errors._general" cols="12">
      <VAlert color="error" variant="tonal" density="compact">{{ errors._general[0] }}</VAlert>
    </VCol>

    <VCol cols="12" md="8">
      <VCard>
        <VCardText class="pa-6">
          <!-- Section 1: Info -->
          <p class="text-subtitle-1 font-weight-semibold mb-4">1. Informasi Sesi</p>
          <VRow class="mb-2">
            <VCol cols="12" md="6">
              <VTextField
                v-model="form.opname_date"
                label="Tanggal Opname *"
                type="date"
                variant="outlined"
                :error-messages="errors?.opname_date"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.notes"
                label="Catatan (Opsional)"
                placeholder="Misal: Opname bulanan rutin, opname pasca event, dll."
                rows="2"
                variant="outlined"
                :error-messages="errors?.notes"
                counter="500"
                maxlength="500"
              />
            </VCol>
          </VRow>

          <VDivider class="mb-6" />

          <!-- Section 2: Pilih Bahan Baku -->
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center gap-2">
              <p class="text-subtitle-1 font-weight-semibold mb-0">2. Pilih Bahan Baku</p>
              <VChip
                v-if="form.ingredient_ids.length > 0"
                size="small"
                color="primary"
              >
                {{ form.ingredient_ids.length }} dipilih
              </VChip>
            </div>
            <VBtn
              variant="tonal"
              size="small"
              :color="allSelected ? 'error' : 'secondary'"
              @click="toggleSelectAll"
            >
              {{ allSelected ? 'Batal Semua' : 'Pilih Semua' }}
            </VBtn>
          </div>

          <VTextField
            v-model="ingredientSearch"
            placeholder="Cari nama bahan baku..."
            prepend-inner-icon="ri-search-line"
            density="compact"
            variant="outlined"
            class="mb-2"
            clearable
            hide-details
          />

          <VAlert
            v-if="errors?.ingredient_ids"
            color="error"
            variant="tonal"
            density="compact"
            class="mb-2"
          >
            {{ Array.isArray(errors.ingredient_ids) ? errors.ingredient_ids[0] : errors.ingredient_ids }}
          </VAlert>

          <!-- List Ingredients -->
          <div class="ingredient-list rounded border" style="max-height: 300px; overflow-y: auto">
            <VProgressLinear v-if="ingredientsLoading" indeterminate color="primary" />

            <template v-if="!ingredientsLoading">
              <div v-if="ingredients.length === 0" class="text-center py-6 text-medium-emphasis">
                <VIcon icon="ri-inbox-line" size="32" class="mb-1" />
                <p class="text-body-2">Tidak ada bahan baku</p>
              </div>

              <VCheckbox
                v-for="ingredient in ingredients"
                :key="ingredient.id"
                v-model="form.ingredient_ids"
                :value="ingredient.id"
                density="compact"
                hide-details
                class="px-3"
              >
                <template #label>
                  <div class="d-flex align-center justify-space-between w-100 py-1">
                    <span class="text-body-2">{{ ingredient.name }}</span>
                    <span class="text-caption text-medium-emphasis">
                      {{ ingredient.current_stock ?? ingredient.stock_quantity ?? 0 }} {{ ingredient.unit?.symbol }}
                    </span>
                  </div>
                </template>
              </VCheckbox>
            </template>
          </div>

          <!-- Actions -->
          <div class="d-flex gap-3 mt-6">
            <VBtn
              color="primary"
              :loading="loading"
              :disabled="form.ingredient_ids.length === 0 || !form.opname_date"
              prepend-icon="ri-clipboard-line"
              @click="submit"
            >
              Buat Sesi Opname
            </VBtn>
            <VBtn
              variant="tonal"
              color="secondary"
              :disabled="loading"
              :to="{ name: 'inventory-stock-opnames' }"
            >
              Batal
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Info Panel -->
    <VCol cols="12" md="4">
      <VCard color="info" variant="tonal">
        <VCardText>
          <div class="d-flex align-center gap-2 mb-3">
            <VIcon icon="ri-lightbulb-line" />
            <span class="font-weight-semibold">Cara Kerja</span>
          </div>
          <ol class="text-body-2 ps-4">
            <li class="mb-2">Pilih bahan baku yang akan dihitung fisik</li>
            <li class="mb-2">Sistem catat stok sistem saat ini sebagai <em>snapshot</em></li>
            <li class="mb-2">Input jumlah stok aktual di halaman detail</li>
            <li>Konfirmasi → stok disesuaikan otomatis</li>
          </ol>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped>
.ingredient-list {
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
